import React from "react";

// Functional line schematics for the three work records. Every stroke carries
// pathLength="1" so CSS can draw it in when the record reveals.
let seq = 0;
const d = () => ({ style: { "--d": seq++ } });

function Node({ x, y, w, h = 44, label, sub }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="3" pathLength="1" {...d()} />
      <text x={x + 10} y={y + (sub ? 19 : h / 2 + 4)} className="s-label" {...d()}>
        {label}
      </text>
      {sub && (
        <text x={x + 10} y={y + 33} className="s-sub" {...d()}>
          {sub}
        </text>
      )}
    </g>
  );
}

function Mini({ x, y, w = 48, label }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height="24" rx="2" pathLength="1" {...d()} />
      <text x={x + w / 2} y={y + 16} className="s-mini" textAnchor="middle" {...d()}>
        {label}
      </text>
    </g>
  );
}

function Edge({ d: path, label, lx, ly }) {
  return (
    <g>
      <path d={path} pathLength="1" {...d()} />
      {label && (
        <text x={lx} y={ly} className="s-edge" textAnchor="middle" {...d()}>
          {label}
        </text>
      )}
    </g>
  );
}

function Tokenization() {
  seq = 0;
  return (
    <svg
      viewBox="0 0 360 210"
      role="img"
      aria-label="Schematic: an issuer mints a security token governed by ERC-3643 or CMTAT; the token checks an OnchainID identity registry and transfer rules before an investor can hold it."
    >
      <Node x={8} y={24} w={70} label="Issuer" />
      <Edge d="M78 46 H118" label="mint" lx={98} ly={40} />
      <Node x={118} y={24} w={130} label="Security token" sub="ERC-3643 · CMTAT" />
      <Edge d="M248 46 H280" label="transfer" lx={264} ly={84} />
      <Node x={280} y={24} w={72} label="Investor" />
      <Edge d="M158 68 V140" label="checks" lx={178} ly={108} />
      <Node x={96} y={140} w={124} label="Identity registry" sub="OnchainID" />
      <Edge d="M220 162 H236" />
      <Node x={236} y={140} w={116} label="Transfer rules" sub="Compliance" />
      <Edge d="M316 140 V68" label="gate" lx={332} ly={108} />
    </svg>
  );
}

function Custody() {
  seq = 0;
  const chains = ["Bitcoin", "EVM", "Solana", "IOTA", "Canton"];
  return (
    <svg
      viewBox="0 0 360 210"
      role="img"
      aria-label="Schematic: a policy layer with limits and approvals feeds an m-of-n multisig signer written in Rust, which writes an audit trail and drives per-chain adapters for Bitcoin, EVM, Solana, IOTA, and Canton."
    >
      <Node x={8} y={24} w={126} label="Policy" sub="Limits · approvals" />
      <Edge d="M134 46 H150" />
      <Node x={150} y={24} w={100} label="Multisig" sub="m-of-n · Rust" />
      <Edge d="M250 46 H276" />
      <Node x={276} y={24} w={76} label="Adapters" sub="per chain" />
      <Edge d="M200 68 V110 H58 V140" label="log" lx={70} ly={104} />
      <Node x={8} y={140} w={100} label="Audit trail" sub="every action" />
      <Edge d="M314 68 V120" />
      <Edge d="M136 120 H328" />
      {chains.map((c, i) => (
        <g key={c}>
          <Edge d={`M${136 + i * 48} 120 V150`} />
          <Mini x={112 + i * 48} y={150} label={c} />
        </g>
      ))}
    </svg>
  );
}

function Defi() {
  seq = 0;
  const protos = ["Uniswap", "Aave", "Morpho", "Lido", "Euler"];
  return (
    <svg
      viewBox="0 0 360 210"
      role="img"
      aria-label="Schematic: the protocol layer combines an AMM, lending, and options; positions execute privately on ZKSync Prividium and DAML on Canton before confidential settlement, with integrations to Uniswap, Aave, Morpho, Lido, and Euler."
    >
      <Node x={8} y={24} w={160} label="Protocol layer" sub="AMM · Lending · Options" />
      <Edge d="M168 46 H184" />
      <Node x={184} y={24} w={168} label="Private execution" sub="Prividium · Canton" />
      <Edge d="M268 68 V96" />
      <Node x={184} y={96} w={168} h={40} label="Settlement" sub="Confidential, final" />
      <Edge d="M40 68 V146 H332" label="integrations" lx={110} ly={140} />
      {protos.map((p, i) => (
        <g key={p}>
          <Edge d={`M${39 + i * 66} 146 V164`} />
          <Mini x={8 + i * 66} y={164} w={62} label={p} />
        </g>
      ))}
    </svg>
  );
}

function Agents() {
  seq = 0;
  return (
    <svg
      viewBox="0 0 360 210"
      role="img"
      aria-label="Schematic: an AI agent sends an intent to a broker that applies spending policy and compliance checks; a multisig wallet signs, every action is written to an audit log, and the output returns to the agent."
    >
      <Node x={8} y={24} w={70} label="Agent" sub="Intent" />
      <Edge d="M78 46 H118" label="asks" lx={98} ly={40} />
      <Node x={118} y={24} w={124} label="Broker" sub="Policy · approvals" />
      <Edge d="M242 46 H282" label="signs" lx={262} ly={40} />
      <Node x={282} y={24} w={70} label="Wallet" sub="Multisig" />
      <Edge d="M200 68 V140" label="checks" lx={222} ly={108} />
      <Node x={118} y={140} w={124} label="Compliance rules" sub="Limits · allowlist" />
      <Edge d="M317 68 V140" label="log" lx={333} ly={108} />
      <Node x={262} y={140} w={90} label="Audit log" sub="Every action" />
      <Edge d="M140 68 V112 H43 V68" label="output" lx={76} ly={106} />
    </svg>
  );
}

const MAP = { tokenization: Tokenization, custody: Custody, defi: Defi, agents: Agents };

export default function Schematic({ id }) {
  const Figure = MAP[id];
  if (!Figure) return null;
  return (
    <figure className="schematic">
      <Figure />
    </figure>
  );
}
